import { formatChatForDisplay } from './chat_format_handler';

// Utility function to validate image count
export const validateImageCount = ({
    currentImagesCount,
    newImagesCount,
    toast
}) => {
    // Check if images exceed the maximum limit of 10
    if (currentImagesCount + newImagesCount > 10) {
        toast({
            title: 'Too many images',
            description: 'You cannot upload more than 10 images at once',
            status: 'error',
            duration: 3000,
            isClosable: true,
        });
        return false;
    }
    return true;
};

// Utility function to validate document count
export const validateDocumentCount = ({
    currentDocumentsCount,
    newDocumentsCount,
    toast
}) => {
    // Check if documents exceed the maximum limit of 1
    if (currentDocumentsCount + newDocumentsCount > 3) {
        toast({
            title: 'Too many documents',
            description: 'You cannot upload more than 3 documents at once',
            status: 'error',
            duration: 3000,
            isClosable: true,
        });
        return false;
    }
    return true;
};

const validateMessageSubmission = ({
    message,
    images = [],
    documents = [],
    selectedModel,
    modelCapabilities,
    provider,
    currentMessages,
    toast
}) => {
    // Check if any files are being uploaded without model support
    if ((images.length > 0 || documents.length > 0) && !selectedModel) {
        toast({
            title: 'Model Required',
            description: 'Please select a model before uploading files',
            status: 'error',
            duration: 3000,
            isClosable: true,
        });
        return false;
    }

    if (!validateImageCount({
        currentImagesCount: images.length,
        newImagesCount: 0,
        toast
    })) {
        return false;
    }
    
    // Validate document count
    if (!validateDocumentCount({
        currentDocumentsCount: documents.length,
        newDocumentsCount: 0,
        toast
    })) {
        return false;
    }

    if (images.length > 0 && !modelCapabilities.supports_image) {
        toast({
            title: 'Images not supported',
            description: 'The selected model does not support image input',
            status: 'error',
            duration: 3000,
            isClosable: true,
        });
        return false;
    }

    if (documents.length > 0 && !modelCapabilities.supports_documents) {
        toast({
            title: 'Documents not supported',
            description: 'The selected model does not support document uploads',
            status: 'error',
            duration: 3000,
            isClosable: true,
        });
        return false;
    }

    if (documents.length > 0) {
        for (const doc of documents) {
            const fileExtension = doc.name.split('.').pop().toLowerCase();
            if (!modelCapabilities.supported_file_formats.includes(fileExtension)) {
                toast({
                    title: 'Unsupported file format',
                    description: `Supported formats: ${modelCapabilities.supported_file_formats.join(', ')}`,
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
                return false;
            }
        }
    }

     const allFiles = [...images, ...documents];
     for (const file of allFiles) {
         if (file.size > modelCapabilities.max_file_size) {
             toast({
                 title: 'File too large',
                 description: `Maximum file size is ${modelCapabilities.max_file_size / (1024 * 1024)}MB. File "${file.name}" is ${(file.size / (1024 * 1024)).toFixed(2)}MB`,
                 status: 'error',
                 duration: 3000,
                 isClosable: true,
             });
             return false;
         }
     }

    if (provider === 'openai') {
        const hasImagesInHistory = currentMessages.some(msg => {
            if (Array.isArray(msg.content)) {
                return msg.content.some(item => 
                    item.type === 'image_url' || item.type === 'image'
                );
            }
            return msg.images?.length > 0;
        });

        const hasDocumentsInHistory = currentMessages.some(msg => {
            if (msg.document_names?.length > 0 || msg.documents?.length > 0) {
                return true;
            }
            if (typeof msg.content === 'string' && msg.content.includes('[DOCUMENT_REFERENCES:{')) {
                return true;
            }
            return false;
        });

        if (images.length > 0 && (documents.length > 0 || hasDocumentsInHistory)) {
            toast({
                title: 'Cannot mix content types',
                description: 'OpenAI models don\'t support mixing documents and images in the same conversation',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return false;
        }

        if (documents.length > 0 && (images.length > 0 || hasImagesInHistory)) {
            toast({
                title: 'Cannot mix content types',
                description: 'OpenAI models don\'t support mixing documents and images in the same conversation',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return false;
        }
    }

    return true;
};

const handleStreamingError = (error, navigate) => {
    if (
        error.message?.includes('Failed to fetch') ||
        error.message?.includes('CORS') ||
        error.message?.includes('Access-Control-Allow-Origin')
    ) {
        document.cookie = 'auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        navigate('/login');
        return true;
    }
    return false;
};

export const handleSystemPromptChange = (setSystemPrompt, setChatInfo, prompt, modelId) => {
    let cleanedPrompt = prompt.replace(/(Formatting re-enabled\n\n)+/g, "Formatting re-enabled\n\n");
    if (modelId !== "o3-mini-2025-01-31" && modelId !== "o1-2024-12-17") {
        cleanedPrompt = cleanedPrompt.replace("Formatting re-enabled\n\n", "");
    }
    setSystemPrompt(prompt);
    setChatInfo(prev => ({
        ...prev,
        prompt: prompt
    }));
};

export const handleRegenerate = async ({
    messageIndex,
    currentMessages,
    setCurrentMessages,
    selectedChat,
    selectedModel,
    temperature,
    systemPrompt,
    abortControllerRef,
    sendStreamingMessage,
    setIsStreaming
}) => {
    setIsStreaming(true);

    abortControllerRef.current = new AbortController();

    const truncatedMessages = currentMessages.slice(0, -1);
    setCurrentMessages(truncatedMessages);

    try {
        await sendStreamingMessage({
            conversationId: selectedChat.id,
            messages: truncatedMessages,
            model: selectedModel,
            temperature,
            systemPrompt,
            documents: [],
            document_names: [],
            onStream: ({ type, content }) => {
                if (type === 'content') {
                    setCurrentMessages(prev => {
                        const newMessages = [...prev];
                        const lastMessage = newMessages[newMessages.length - 1];
                        if (lastMessage.role === 'assistant') {
                            newMessages[newMessages.length - 1] = {
                                ...lastMessage,
                                content: lastMessage.content + content
                            };
                        } else {
                            newMessages.push({ role: 'assistant', content });
                        }
                        return newMessages;
                    });
                } else if (type === 'citations') {
                    setCurrentMessages(prev => {
                        const newMessages = [...prev];
                        const lastMessage = newMessages[newMessages.length - 1];
                        if (lastMessage && lastMessage.role === 'assistant') {
                            newMessages[newMessages.length - 1] = {
                                ...lastMessage,
                                citations: content
                            };
                        }
                        return newMessages;
                    });
                }
            },
            signal: abortControllerRef.current.signal
        });
    } catch (error) {
        if (error.name === 'AbortError') {
            return;
        }
        console.error('Error in handleRegenerate:', error);
    } finally {
        setIsStreaming(false);
    }
};

export const handleEditChat = async ({
    chat,
    editChatTitle,
    getInitialAppData,
    setChatHistory,
    toast
}) => {
    const newTitle = prompt('Enter new title:', chat.title);
    if (newTitle && newTitle.trim() !== '') {
        const result = await editChatTitle(chat.id, newTitle.trim());
        if (result.success) {
            const historyResult = await getInitialAppData();
            if (historyResult.success) {
                setChatHistory(historyResult.data.chat_titles);
            }
        } else {
            toast({
                title: 'Failed to update chat title',
                description: result.error,
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    }
};

export const handleDeleteChat = async ({
    chat,
    selectedChat,
    handleNewChat,
    deleteChat,
    getInitialAppData,
    setChatHistory,
    toast
}) => {
    if (window.confirm('Are you sure you want to delete this chat?')) {
        const result = await deleteChat(chat.id);
        if (result.success) {
            if (selectedChat?.id === chat.id) {
                handleNewChat();
            }
            const historyResult = await getInitialAppData();
            if (historyResult.success) {
                setChatHistory(historyResult.data.chat_titles);
            }
        } else {
            toast({
                title: 'Failed to delete chat',
                description: result.error,
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    }
};

export const handleSelectChat = async ({
    chat,
    setIsLoading,
    setSelectedChat,
    setIsNewChat,
    navigate,
    getChatById,
    setSelectedModel,
    setTemperature,
    setSystemPrompt,
    setCurrentMessages,
    toast,
    isStreaming,
    handleStopStream
}) => {

    setIsLoading(true);

    try {
        if (isStreaming && handleStopStream) {
            await handleStopStream();
        }

        const result = await getChatById(chat.id);

        if (result.success) {
            setSelectedChat(chat);
            setIsNewChat(false);
            navigate(`/chat/${chat.id}`);
            const modelId = result.data.conversation.model;
            const formattedData = formatChatForDisplay(result.data, modelId);

            setSelectedModel(modelId);
            setTemperature(result.data.conversation.temperature);
            setSystemPrompt(formattedData.systemPrompt);
            setCurrentMessages(formattedData.messages);
            return modelId;
        } else {
            toast({
                title: 'Failed to load chat',
                description: result.error,
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return null;
        }
    } catch (error) {
        toast({
            title: 'Error',
            description: error.message,
            status: 'error',
            duration: 3000,
            isClosable: true,
        });
        return null;
    } finally {
        setIsLoading(false);
    }
};

export const handleModelSelect = ({
    modelId,
    modelList,
    setSelectedModel,
    setSelectedProvider,
    setSelectedModelCapabilities
}) => {
    let selectedModelData = null;
    let provider = '';

    for (const providerData of modelList) {
        const model = providerData.models.find(m => m.id === modelId);
        if (model) {
            selectedModelData = model;
            provider = providerData.provider;
            break;
        }
    }

    if (selectedModelData) {
        setSelectedModel(modelId);
        setSelectedProvider(provider);
        setSelectedModelCapabilities({
            supports_image: selectedModelData.supports_image,
            supports_documents: selectedModelData.supports_documents,
            max_file_size: selectedModelData.max_file_size,
            supported_file_formats: selectedModelData.supported_file_formats
        });
    }
};

export const handleTemperatureChange = (value, setTemperature, setChatInfo) => {
    setTemperature(value);
    setChatInfo(prev => ({
        ...prev,
        temperature: value
    }));
};

export const handleMessageSubmit = async ({
    message,
    images = [],
    documents = [],
    abortControllerRef,
    selectedModel,
    isNewChat,
    toast,
    setCurrentMessages,
    setMessage,
    selectedChat,
    systemPrompt,
    temperature,
    setIsNewChat,
    setSelectedChat,
    navigate,
    getInitialAppData,
    setChatHistory,
    createStreamingChat,
    sendStreamingMessage,
    currentMessages,
    setIsStreaming,
    setIsFirstMessageNavigation,
    setIsWaitingForFirstResponse,
    modelCapabilities,
    provider
}) => {
    // First validate the submission
    const isValid = validateMessageSubmission({
        message,
        images,
        documents,
        selectedModel,
        modelCapabilities,
        provider,
        currentMessages,
        toast
    });

    if (!isValid) {
        setIsWaitingForFirstResponse(false);
        setIsStreaming(false);
        return;
    }

    setIsStreaming(true);
    setIsWaitingForFirstResponse(true);
    let errorOccurred = false;
    const originalMessage = message;

    abortControllerRef.current = new AbortController();
    if (!message.trim() && images.length === 0 && documents.length === 0) {
        toast({
            title: 'Please enter a message, add an image, or upload a document',
            status: 'error',
            duration: 3000,
            isClosable: true,
        });
        setIsWaitingForFirstResponse(false);
        setIsStreaming(false);
        return;
    }

    if (!selectedModel && isNewChat) {
        toast({
            title: 'Please select a model',
            status: 'error',
            duration: 3000,
            isClosable: true,
        });
        setIsWaitingForFirstResponse(false);
        setIsStreaming(false);
        return;
    }

    // Convert images to base64
    const imagePromises = images.map(image =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const base64String = reader.result.split(',')[1];
                resolve(base64String);
            };
            reader.onerror = reject;
            reader.readAsDataURL(image);
        })
    );

    // Convert documents to base64
    const documentPromises = documents.map(doc =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const base64String = reader.result.split(',')[1];
                resolve({
                    content: base64String,
                    name: doc.name
                });
            };
            reader.onerror = reject;
            reader.readAsDataURL(doc);
        })
    );

    let base64Images = [];
    let documentData = [];
    try {
        base64Images = await Promise.all(imagePromises);
        documentData = await Promise.all(documentPromises);
    } catch (error) {
        toast({
            title: 'Failed to process uploads',
            status: 'error',
            duration: 3000,
            isClosable: true,
        });
        setIsWaitingForFirstResponse(false);
        setIsStreaming(false);
        return;
    }

    const userMessage = {
        role: 'user',
        content: message,
        images: base64Images,
        documents: documentData.map(doc => doc.content),
        document_names: documentData.map(doc => doc.name)
    };

    setCurrentMessages(prev => [...prev, userMessage]);
    setMessage('');

    try {
        const currentConversationId = selectedChat?.id;

        const extractCot = (content) => {
            try {
                const jsonMatch = content.match(/(\{.*?\})(.*)/s);
                if (!jsonMatch) {
                    return { cot: null, cleanContent: content };
                }

                const [_, jsonStr, remainingContent] = jsonMatch;
                const parsed = JSON.parse(jsonStr);

                if (parsed.cot) {
                    return {
                        cot: parsed.cot,
                        cleanContent: remainingContent.trim()
                    };
                }
                
                return { cot: null, cleanContent: content };
            } catch (e) {
                console.error('[Cot Parser] JSON parse error:', e.message);
                return { cot: null, cleanContent: content };
            }
        };

        const extractCitations = (content) => {
            try {
                const citationsMatch = content.match(/\{"citations":\s*(\[.*?\])\}/);
                if (!citationsMatch) {
                    return { citations: null, cleanContent: content };
                }

                const citationsArray = JSON.parse(citationsMatch[1]);
                const cleanContent = content.replace(/\{"citations":\s*\[.*?\]\}/, '').trim();
                
                return {
                    citations: citationsArray,
                    cleanContent: cleanContent
                };
            } catch (e) {
                console.error('[Citations Parser] JSON parse error:', e.message);
                return { citations: null, cleanContent: content };
            }
        };

        const handleStream = ({ type, content, conversationId, error }) => {
            if (type === 'error' && !errorOccurred) {
                errorOccurred = true;
                setCurrentMessages(prev => prev.slice(0, -1));
                setIsWaitingForFirstResponse(false);
                setIsStreaming(false);
                toast({
                    title: 'Error',
                    description: error,
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
                return;
            }

            if (type === 'content') {
                setIsWaitingForFirstResponse(false);
                setCurrentMessages(prev => {
                    const newMessages = [...prev];
                    let lastMessage = newMessages[newMessages.length - 1];
                    if (!lastMessage || lastMessage.role !== 'assistant') {
                        newMessages.push({ role: 'assistant', content: '' });
                        lastMessage = newMessages[newMessages.length - 1];
                    }
                    
                    const accumulatedContent = (lastMessage.content || '') + content;
                    
                    // First check for citations in the content
                    const { citations, cleanContent: contentWithoutCitations } = extractCitations(accumulatedContent);
                    
                    // Then check for CoT in the remaining content
                    const { cot, cleanContent: finalContent } = extractCot(contentWithoutCitations);

                    newMessages[newMessages.length - 1] = {
                        ...lastMessage,
                        content: finalContent,
                        ...(cot && { cot: (lastMessage.cot || '') + cot }),
                        ...(citations && { citations })
                    };

                    return newMessages;
                });
            } else if (type === 'citations') {
                setCurrentMessages(prev => {
                    const newMessages = [...prev];
                    const lastMessage = newMessages[newMessages.length - 1];
                    if (lastMessage && lastMessage.role === 'assistant') {
                        newMessages[newMessages.length - 1] = {
                            ...lastMessage,
                            citations: content
                        };
                    }
                    return newMessages;
                });
            } else if (type === 'url_correction') {
                setCurrentMessages(prev => {
                    const newMessages = [...prev];
                    const lastMessage = newMessages[newMessages.length - 1];
                    if (lastMessage && lastMessage.role === 'assistant') {
                        console.log('Applying URL correction, replacing content...');
                        const { citations, cleanContent: contentWithoutCitations } = extractCitations(content);
                        const { cot, cleanContent: finalContent } = extractCot(contentWithoutCitations);
                        newMessages[newMessages.length - 1] = {
                            ...lastMessage,
                            content: finalContent,
                            ...(cot && { cot }),
                            ...(citations && { citations })
                        };
                    }
                    return newMessages;
                });
            } else if (type === 'id' && !currentConversationId) {
                setIsFirstMessageNavigation(true);
                setIsNewChat(false);
                setSelectedChat(prev => ({ ...prev, id: conversationId }));
                navigate(`/chat/${conversationId}`, { replace: true });

                getInitialAppData().then(result => {
                    if (result.success) {
                        setChatHistory(result.data.chat_titles);
                    }
                });
            } else if (data.event === "[DONE]") {
                return;
            } else if (data.citations) {
                setCurrentMessages(prev => {
                    const newMessages = [...prev];
                    const lastMessage = newMessages[newMessages.length - 1];
                    if (lastMessage && lastMessage.role === 'assistant') {
                        newMessages[newMessages.length - 1] = {
                            ...lastMessage,
                            citations: data.citations
                        };
                    }
                    return newMessages;
                });
            } else if (data.content) {
                setCurrentMessages(prev => {
                    const newMessages = [...prev];
                    const lastMessage = newMessages[newMessages.length - 1];
                    if (lastMessage && lastMessage.role === 'assistant') {
                        newMessages[newMessages.length - 1] = {
                            ...lastMessage,
                            content: lastMessage.content + data.content
                        };
                    } else {
                        newMessages.push({ role: 'assistant', content: data.content });
                    }
                    return newMessages;
                });
            } else if (data.error) {
                // ... existing code ...
            }
        };

        if (!currentConversationId) {
            await createStreamingChat({
                model: selectedModel,
                message: userMessage.content,
                systemPrompt,
                temperature,
                images: base64Images,
                documents: documentData.map(doc => doc.content),
                document_names: documentData.map(doc => doc.name),
                onStream: handleStream,
                signal: abortControllerRef.current.signal
            });
        } else {
            const messagesToSend = [...currentMessages, userMessage];
            await sendStreamingMessage({
                conversationId: currentConversationId,
                messages: messagesToSend,
                model: selectedModel,
                temperature,
                systemPrompt,
                documents: documentData.map(doc => doc.content),
                document_names: documentData.map(doc => doc.name),
                onStream: handleStream,
                signal: abortControllerRef.current.signal
            });
        }
    } catch (error) {
        if (!errorOccurred) {
            console.error('Error in message submission:', error);

            if (error.name === 'AbortError') {
                setCurrentMessages(prev => prev.slice(0, -1));
                setIsWaitingForFirstResponse(false);
                setIsStreaming(false);
                return;
            }

            if (handleStreamingError(error, navigate)) {
                setCurrentMessages(prev => prev.slice(0, -1));
                setIsWaitingForFirstResponse(false);
                setIsStreaming(false);
                return;
            }

            setCurrentMessages(prev => prev.slice(0, -1));
            setIsWaitingForFirstResponse(false);
            setIsStreaming(false);

            toast({
                title: 'Error',
                description: error.message || 'An unexpected error occurred',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    } finally {
        if (errorOccurred) {
            setMessage(originalMessage);
            setIsWaitingForFirstResponse(false);
            setIsStreaming(false);
            setCurrentMessages(prev => prev.slice(0, -1));
        }
        setIsStreaming(false);
    }
};

export const handleEditMessage = async ({
    messageIndex,
    newContent,
    images,
    currentMessages,
    setCurrentMessages,
    selectedChat,
    selectedModel,
    temperature,
    systemPrompt,
    abortControllerRef,
    sendStreamingMessage,
    setIsStreaming,
    setIsWaitingForEditResponse
}) => {
    setIsStreaming(true);
    setIsWaitingForEditResponse(true);
    abortControllerRef.current = new AbortController();

    const messages = [...currentMessages];
    if (messages[messages.length - 1].role === 'assistant') {
        messages.pop();
    }

    const lastMessage = messages[messages.length - 1];
    messages[messages.length - 1] = {
        ...lastMessage,
        content: newContent,
        images: lastMessage.images || [] 
    };

    setCurrentMessages([...messages, { role: 'assistant', content: '.' }]);

    try {
        let chatId = selectedChat?.id;
        if (!chatId) {
            const pathParts = window.location.pathname.split('/');
            chatId = pathParts[pathParts.length - 1];
        }


        await sendStreamingMessage({
            conversationId: chatId,
            messages: messages,
            model: selectedModel,
            temperature,
            systemPrompt,
            documents: [],
            document_names: [],
            onStream: ({ type, content }) => {
                if (type === 'content') {
                    setIsWaitingForEditResponse(false);
                    setCurrentMessages(prev => {
                        const newMessages = [...prev];
                        const lastMsg = newMessages[newMessages.length - 1];
                        if (lastMsg.role === 'assistant' && lastMsg.content === '.') {
                            return [...messages, { role: 'assistant', content }];
                        } else if (lastMsg.role === 'assistant') {
                            newMessages[newMessages.length - 1] = {
                                ...lastMsg,
                                content: lastMsg.content + content
                            };
                            return newMessages;
                        } else {
                            return [...newMessages, { role: 'assistant', content }];
                        }
                    });
                } else if (type === 'citations') {
                    setCurrentMessages(prev => {
                        const newMessages = [...prev];
                        const lastMsg = newMessages[newMessages.length - 1];
                        if (lastMsg && lastMsg.role === 'assistant') {
                            newMessages[newMessages.length - 1] = {
                                ...lastMsg,
                                citations: content
                            };
                        }
                        return newMessages;
                    });
                }
            },
            signal: abortControllerRef.current.signal
        });
    } catch (error) {
        if (error.name === 'AbortError') {
            setCurrentMessages(messages);
            return;
        }
        console.error('Error in handleEditMessage:', error);
        setCurrentMessages(messages);
    } finally {
        setIsStreaming(false);
        setIsWaitingForEditResponse(false);
    }
};
